import React from "react";
import { useLocalization } from "../hooks/useLocalization";

const COLOR_MAP = {
  APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  CLEARED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  CLEARED_TO_WORK: "bg-emerald-100 text-emerald-700 border-emerald-300",
  PUBLISHED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  FULLY_EXECUTED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  ACTIVE: "bg-emerald-100 text-emerald-700 border-emerald-300",
  REJECTED: "bg-red-100 text-red-700 border-red-300",
  BLOCKED: "bg-red-100 text-red-700 border-red-300",
  NOT_CLEARED: "bg-red-100 text-red-700 border-red-300",
  NEEDS_CORRECTION: "bg-red-100 text-red-700 border-red-300",
  EXPIRED: "bg-red-100 text-red-700 border-red-300",
  UNDER_REVIEW: "bg-amber-100 text-amber-800 border-amber-300",
  SUBMITTED: "bg-amber-100 text-amber-800 border-amber-300",
  PENDING_REVIEW: "bg-amber-100 text-amber-800 border-amber-300",
  WAITING_FOR_EMPLOYEE: "bg-amber-100 text-amber-800 border-amber-300",
  WAITING_FOR_MANAGEMENT: "bg-amber-100 text-amber-800 border-amber-300",
  AWAITING_EMPLOYEE_SIGNATURE: "bg-amber-100 text-amber-800 border-amber-300",
  AWAITING_COMPANY_SIGNATURE: "bg-amber-100 text-amber-800 border-amber-300",
  OVERRIDDEN: "bg-blue-100 text-blue-700 border-blue-300",
  NOT_APPLICABLE: "bg-gray-100 text-gray-500 border-gray-300",
  WAIVED: "bg-gray-100 text-gray-500 border-gray-300",
  NOT_STARTED: "bg-gray-100 text-gray-600 border-gray-300",
  DRAFT: "bg-gray-100 text-gray-600 border-gray-300",
  PLAN_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  EVIDENCE_PENDING: "bg-orange-100 text-orange-700 border-orange-300",
  MANAGEMENT_REVIEW: "bg-amber-100 text-amber-800 border-amber-300",
  READY_TO_SUBMIT: "bg-blue-100 text-blue-700 border-blue-300",
  FOLLOW_UP_REQUIRED: "bg-orange-100 text-orange-700 border-orange-300",
  INACTIVE: "bg-amber-100 text-amber-800 border-amber-300",
  NO_LONGER_WORKING: "bg-gray-200 text-gray-600 border-gray-400",
  SUSPENDED: "bg-red-100 text-red-700 border-red-300",
  // Article 36 agreement lifecycle
  REPRESENTATIVE_SELECTION_PENDING: "bg-amber-100 text-amber-800 border-amber-300",
  EMPLOYEE_VOTE_PENDING: "bg-amber-100 text-amber-800 border-amber-300",
  SIGNATURE_PENDING: "bg-amber-100 text-amber-800 border-amber-300",
  READY_TO_FILE: "bg-blue-100 text-blue-700 border-blue-300",
  FILED: "bg-blue-100 text-blue-700 border-blue-300",
  SCHEDULED: "bg-blue-100 text-blue-700 border-blue-300",
  FINALIZED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  MISSING_BLUE: "bg-blue-100 text-blue-700 border-blue-300",
  MISSING_RED: "bg-red-100 text-red-700 border-red-300",
  PROPOSED_PAIR: "bg-amber-100 text-amber-800 border-amber-300",
  PAIRED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  PAIR_REJECTED: "bg-rose-100 text-rose-700 border-rose-300",
  EXPIRING: "bg-amber-100 text-amber-800 border-amber-300",
  ARTICLE_36_NOT_ACTIVE: "bg-red-100 text-red-700 border-red-300",
  // Election statuses
  OPEN: "bg-amber-100 text-amber-800 border-amber-300",
  CLOSED: "bg-blue-100 text-blue-700 border-blue-300",
  CERTIFIED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  // Notice of Employment Conditions
  ISSUED: "bg-blue-100 text-blue-700 border-blue-300",
  ACKNOWLEDGED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  // Employee application
  UNDER_MANAGEMENT_REVIEW: "bg-amber-100 text-amber-800 border-amber-300",
  INFORMATION_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  WITHDRAWN: "bg-gray-100 text-gray-500 border-gray-300",
  CONVERTED_TO_EMPLOYEE: "bg-emerald-100 text-emerald-700 border-emerald-300",
  // Recruitment (careers portal applicants / interview slots)
  NEW: "bg-blue-100 text-blue-700 border-blue-300",
  SCREENING: "bg-amber-100 text-amber-800 border-amber-300",
  INTERVIEW_SCHEDULED: "bg-blue-100 text-blue-700 border-blue-300",
  INTERVIEWED: "bg-blue-100 text-blue-700 border-blue-300",
  OFFER_EXTENDED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  HIRED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  AVAILABLE: "bg-emerald-100 text-emerald-700 border-emerald-300",
  BOOKED: "bg-blue-100 text-blue-700 border-blue-300",
  // Interview scheduling engine (2-hour protected-block bookings)
  HELD: "bg-blue-100 text-blue-700 border-blue-300",
  PENDING_CONFIRMATION: "bg-amber-100 text-amber-800 border-amber-300",
  CONFIRMED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  RESCHEDULE_REQUESTED: "bg-orange-100 text-orange-700 border-orange-300",
  RESCHEDULED: "bg-gray-100 text-gray-500 border-gray-300",
  CANCELLED_BY_APPLICANT: "bg-gray-100 text-gray-500 border-gray-300",
  CANCELLED_BY_MANAGEMENT: "bg-gray-100 text-gray-500 border-gray-300",
  NO_SHOW: "bg-red-100 text-red-700 border-red-300",
  // Payroll (wage profiles / rules / periods)
  PENDING_APPROVAL: "bg-amber-100 text-amber-800 border-amber-300",
  SUPERSEDED: "bg-gray-100 text-gray-500 border-gray-300",
  NOT_READY: "bg-gray-100 text-gray-600 border-gray-300",
  READY: "bg-blue-100 text-blue-700 border-blue-300",
  CALCULATED: "bg-blue-100 text-blue-700 border-blue-300",
  SUBMITTED_FOR_REVIEW: "bg-amber-100 text-amber-800 border-amber-300",
  REVIEWED_APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  RETURNED_FOR_CORRECTION: "bg-red-100 text-red-700 border-red-300",
  OPEN_DISPUTE: "bg-red-100 text-red-700 border-red-300",
  RESOLVED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  // Medical check tracking
  NOT_YET_ASSIGNED: "bg-gray-100 text-gray-600 border-gray-300",
  ON_TRACK: "bg-emerald-100 text-emerald-700 border-emerald-300",
  DUE_SOON: "bg-amber-100 text-amber-800 border-amber-300",
  OVERDUE: "bg-red-100 text-red-700 border-red-300",
  APPOINTMENT_SCHEDULED: "bg-blue-100 text-blue-700 border-blue-300",
  // Training / LMS
  REGISTERED: "bg-gray-100 text-gray-600 border-gray-300",
  PARSING: "bg-amber-100 text-amber-800 border-amber-300",
  PARSED: "bg-blue-100 text-blue-700 border-blue-300",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  PARTIALLY_APPROVED: "bg-amber-100 text-amber-800 border-amber-300",
  UNDER_LEGAL_REVIEW: "bg-amber-100 text-amber-800 border-amber-300",
  REPLACED: "bg-gray-100 text-gray-500 border-gray-300",
  ARCHIVED: "bg-gray-100 text-gray-500 border-gray-300",
  ASSIGNED: "bg-blue-100 text-blue-700 border-blue-300",
  IN_PROGRESS: "bg-blue-100 text-blue-700 border-blue-300",
  QUIZ_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  PRACTICAL_REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  RETRAINING_REQUIRED: "bg-red-100 text-red-700 border-red-300",
  MANAGER_APPROVAL_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  COMPLETED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  FAILED: "bg-red-100 text-red-700 border-red-300",
  PASSED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  NOT_APPLICABLE_TRAINING: "bg-gray-100 text-gray-500 border-gray-300",
  // Leave ledger
  GRANT: "bg-emerald-100 text-emerald-700 border-emerald-300",
  USE: "bg-blue-100 text-blue-700 border-blue-300",
  FUTURE_RESERVATION: "bg-amber-100 text-amber-800 border-amber-300",
  CANCELLATION: "bg-gray-100 text-gray-500 border-gray-300",
  CARRY_FORWARD: "bg-blue-100 text-blue-700 border-blue-300",
  EXPIRATION: "bg-red-100 text-red-700 border-red-300",
  MANUAL_ADJUSTMENT: "bg-purple-100 text-purple-700 border-purple-300",
  CORRECTION: "bg-purple-100 text-purple-700 border-purple-300",
  LEAVE_CALCULATION_REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  PENDING: "bg-amber-100 text-amber-800 border-amber-300",
  CANCELLED: "bg-gray-100 text-gray-500 border-gray-300",
  ELIGIBLE: "bg-emerald-100 text-emerald-700 border-emerald-300",
  // Mandatory paid-leave-usage tracking
  CALCULATION_PENDING: "bg-gray-100 text-gray-600 border-gray-300",
  ACTION_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  DEADLINE_APPROACHING: "bg-orange-100 text-orange-700 border-orange-300",
  WAIVED_BY_CORRECTION: "bg-gray-100 text-gray-500 border-gray-300",
  // Holiday & work calendar
  REGULAR_WORKDAY: "bg-gray-100 text-gray-600 border-gray-300",
  COMPANY_HOLIDAY: "bg-blue-100 text-blue-700 border-blue-300",
  STATUTORY_HOLIDAY: "bg-purple-100 text-purple-700 border-purple-300",
  NON_STATUTORY_HOLIDAY: "bg-indigo-100 text-indigo-700 border-indigo-300",
  STORE_CLOSURE: "bg-red-100 text-red-700 border-red-300",
  SPECIAL_OPERATING_DAY: "bg-teal-100 text-teal-700 border-teal-300",
  NOT_CONFIGURED: "bg-gray-100 text-gray-500 border-gray-300",
  // Manual payment confirmation & evidence
  READY_FOR_PAYMENT: "bg-blue-100 text-blue-700 border-blue-300",
  PAYMENT_INITIATED: "bg-amber-100 text-amber-800 border-amber-300",
  PAYMENT_EVIDENCE_PENDING: "bg-orange-100 text-orange-700 border-orange-300",
  PAYMENT_CONFIRMED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  RECONCILIATION_REQUIRED: "bg-red-100 text-red-700 border-red-300",
  PAYMENT_FAILED: "bg-red-100 text-red-800 border-red-400",
  // Google Drive monthly ingestion readiness
  NOT_DISCOVERED: "bg-gray-100 text-gray-500 border-gray-300",
  DISCOVERED: "bg-blue-100 text-blue-700 border-blue-300",
  IMPORTING: "bg-amber-100 text-amber-800 border-amber-300",
  OCR_IN_PROGRESS: "bg-orange-100 text-orange-700 border-orange-300",
  COMPLETE: "bg-emerald-100 text-emerald-700 border-emerald-300",
  PARTIAL: "bg-amber-100 text-amber-800 border-amber-300",
  LOCKED: "bg-gray-200 text-gray-700 border-gray-400",
  DISABLED: "bg-gray-100 text-gray-500 border-gray-300",
  CONNECTED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  CONNECTION_FAILED: "bg-red-100 text-red-700 border-red-300",
  // Email delivery queue
  QUEUED: "bg-gray-100 text-gray-600 border-gray-300",
  SENT: "bg-emerald-100 text-emerald-700 border-emerald-300",
  SUPPRESSED: "bg-gray-100 text-gray-500 border-gray-300",
  // Manager digest severities
  LOW: "bg-gray-100 text-gray-600 border-gray-300",
  MEDIUM: "bg-amber-100 text-amber-800 border-amber-300",
  HIGH: "bg-orange-100 text-orange-700 border-orange-300",
  CRITICAL: "bg-red-100 text-red-800 border-red-400",
  // Data quality findings
  INFO: "bg-slate-100 text-slate-600 border-slate-300",
  WAITING_FOR_EVIDENCE: "bg-orange-100 text-orange-700 border-orange-300",
  ACCEPTED_RISK: "bg-indigo-100 text-indigo-700 border-indigo-300",
  FALSE_POSITIVE: "bg-gray-200 text-gray-600 border-gray-400",
  REOPENED: "bg-red-100 text-red-700 border-red-300",
  // Supply pickup fixed-assignment compensation
  MIN_WAGE_ADJUSTMENT_REQUIRED: "bg-orange-100 text-orange-700 border-orange-300",
  FIXED_ASSIGNMENT_CLASSIFICATION_CONFIRMED: "bg-blue-100 text-blue-700 border-blue-300",
  SOURCE_LABEL_REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  // Employee test-data cleanup classification
  PRODUCTION_CONFIRMED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  MIGRATION_SOURCE: "bg-blue-100 text-blue-700 border-blue-300",
  TEST_AUTOMATION: "bg-red-100 text-red-700 border-red-300",
  DEMO_DATA: "bg-purple-100 text-purple-700 border-purple-300",
  ORPHANED_TEST_DATA: "bg-orange-100 text-orange-700 border-orange-300",
  DUPLICATE_REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  UNCERTAIN_REVIEW_REQUIRED: "bg-gray-200 text-gray-700 border-gray-400",
};

export const StatusBadge = ({ status }) => {
  const { t } = useLocalization();
  const classes = COLOR_MAP[status] || "bg-gray-100 text-gray-600 border-gray-300";
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${classes}`}
    >
      {t(`statuses.${status}`) === `statuses.${status}` ? status : t(`statuses.${status}`)}
    </span>
  );
};
